import { Search, SlidersHorizontal, CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { Input } from '@/shared/ui/Input';
import { Button } from '@/shared/ui/Button';
import { DatePicker } from '@/shared/ui/DatePicker';
import { cn } from '@/shared/lib/utils';
import { ViewModeSwitcher, type ViewMode } from './ViewModeSwitcher';

interface FilterTab {
  key: string;
  label: string;
}

interface AppointmentFilterBarProps {
  tabs: FilterTab[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  selectedDate?: string;
  onDateChange?: (date: string) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  dateFrom?: string;
  dateTo?: string;
  onNavigateRange?: (direction: -1 | 1) => void;
  onToday?: () => void;
}

function formatRangeLabel(mode: ViewMode, dateFrom: string, dateTo: string): string {
  if (mode === 'day') {
    return format(new Date(dateFrom), 'MMM d, yyyy');
  }
  if (mode === 'week') {
    const from = format(new Date(dateFrom), 'MMM d');
    const to = format(new Date(dateTo), 'MMM d, yyyy');
    return `${from} – ${to}`;
  }
  const from = format(new Date(dateFrom), 'MMM d');
  const to = format(new Date(dateTo), 'MMM d, yyyy');
  return `${from} – ${to}`;
}

export function AppointmentFilterBar({
  tabs, activeTab, onTabChange, search, onSearchChange,
  selectedDate, onDateChange,
  viewMode, onViewModeChange, dateFrom, dateTo,
  onNavigateRange, onToday,
}: AppointmentFilterBarProps) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday = selectedDate === todayStr;

  const currentRangeLabel = dateFrom && dateTo && viewMode
    ? formatRangeLabel(viewMode, dateFrom, dateTo)
    : null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl2 border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4 shadow-card">
      <div className="flex h-9 min-w-[220px] flex-1 items-center gap-2 rounded-lg bg-[var(--bg-surface-muted)] px-3">
        <Search className="size-4 text-[var(--text-secondary)]" />
        <Input
          className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--text-secondary)]"
          placeholder="Search by patient, doctor..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-1 rounded-xl bg-[var(--bg-surface-muted)] p-1">
        {tabs.map((tab) => (
          <Button
            key={tab.key}
            variant="ghost"
            onClick={() => onTabChange(tab.key)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-[12px] font-semibold',
              activeTab === tab.key
                ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-soft'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            )}
          >
            {tab.label}
          </Button>
        ))}
      </div>
      {onViewModeChange && viewMode && (
        <ViewModeSwitcher
          value={viewMode}
          onChange={(mode) => onViewModeChange(mode)}
        />
      )}
      {viewMode === 'day' && onDateChange && (
        <div className="flex items-center gap-1">
          <DatePicker
            value={selectedDate}
            onChange={onDateChange}
            placeholder="Filter by date"
          />
          {!isToday && (
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 rounded-lg px-3 text-[13px] font-medium"
              onClick={() => onDateChange(todayStr)}
            >
              <CalendarIcon className="size-3.5" />
              Today
            </Button>
          )}
        </div>
      )}
      {viewMode !== 'day' && dateFrom && dateTo && onNavigateRange && (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => onNavigateRange(-1)}>
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-[13px] font-semibold text-[var(--text-primary)] whitespace-nowrap">
            {currentRangeLabel}
          </span>
          <Button variant="ghost" size="icon" onClick={() => onNavigateRange(1)}>
            <ChevronRight className="size-4" />
          </Button>
          {onToday && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1 rounded-lg px-2 text-[11px] font-medium"
              onClick={onToday}
            >
              <CalendarIcon className="size-3" />
              Today
            </Button>
          )}
        </div>
      )}
      <Button
        variant="outline"
        disabled
        title="Coming soon"
        className="h-9 gap-1.5 rounded-lg border-[var(--border-subtle)] px-3 text-[13px] font-medium text-[var(--text-primary)]"
      >
        <SlidersHorizontal className="size-3.5" />
        More filters
        <span className="text-[11px] font-medium text-[var(--text-secondary)]">Coming soon</span>
      </Button>
    </div>
  );
}

